import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { motion } from "framer-motion"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useEffect, useMemo, useState } from "react"
import {
  usePlayerStats,
  PlayerGameStats,
  PlayerStats,
  PlayerTournamentTypeStats,
  DetailedGameStats,
  GameAnalyzedSeasons,
} from "@/graphql/hooks/usePlayerStats"
import { Archive, Crown, Eye, EyeOff, Gamepad2, Target, TrendingUp } from "lucide-react"
import { SummaryCards } from "./SummaryCards"

interface PlayerStatsDashboardProps {
  personId?: number
  playerCode?: string
  showPlayerIdentity?: boolean
}

const mask = (value: string, visibleChars = 2) => {
  if (!value) return "-"
  return value.slice(0, visibleChars) + "•".repeat(Math.max(4, value.length - visibleChars))
}

const maskEmail = (email: string) => {
  if (!email) return "-"
  const [local, domain] = email.split("@")
  return `${mask(local, 2)}@${domain ? mask(domain, 2) : "•••"}`
}

const PlayerIdentityCard = ({
  player,
}: {
  player: {
    code: string
    name: string
    firstSurname: string
    lastSurname: string
    email: string
    documentNumber: string
  }
}) => {
  const [revealed, setRevealed] = useState(false)

  const fullName = [player.name, player.firstSurname, player.lastSurname].filter(Boolean).join(" ")

  return (
    <Card className="bg-card border border-border">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-foreground text-base">Datos del jugador</CardTitle>
        <button
          onClick={() => setRevealed((r) => !r)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {revealed ? "Ocultar datos" : "Revelar datos"}
        </button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Codigo</p>
            <p className="font-medium text-foreground">{player.code || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Nombre completo</p>
            <p className="font-medium text-foreground">{fullName || "-"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium text-foreground break-all">
              {revealed ? player.email || "-" : maskEmail(player.email)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">Numero documento</p>
            <p className="font-medium text-foreground">
              {revealed ? player.documentNumber || "-" : mask(player.documentNumber, 0)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const DetailedStatsSkeleton = () => (
  <Card className="bg-card border border-border">
    <CardHeader className="flex flex-row items-center gap-2">
      <TrendingUp className="h-5 w-5 text-primary" />
      <CardTitle className="text-foreground">Estadísticas Detalladas</CardTitle>
    </CardHeader>
    <CardContent>
      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 p-1 rounded-md mb-4">
        <Skeleton className="h-8 w-[130px] rounded-md" />
        <Skeleton className="h-8 w-[130px] rounded-md" />
      </div>
      {/* Season filter */}
      <div className="mb-4 rounded-lg border border-border/70 bg-muted/20 p-3">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-md" />
          ))}
        </div>
      </div>
      {/* Type rows */}
      <div className="divide-y divide-border">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="py-3 grid grid-cols-[minmax(0,1fr)_112px] gap-x-4 items-center">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-14" />
              </div>
              <Skeleton className="h-1.5 w-full rounded-full" />
            </div>
            <div className="flex flex-col items-end gap-1">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
)

const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i} className="bg-card border border-border">
          <CardContent className="p-4">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-8 w-16" />
          </CardContent>
        </Card>
      ))}
    </div>
    <Card className="bg-card border border-border">
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-border">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <DetailedStatsSkeleton />
  </div>
)

const GAME_THEME_DEFAULT = {
  gradient: "from-primary/20 to-primary/5",
  icon: "bg-primary/15 text-primary",
  ring: "ring-primary/20",
  bar: "from-primary to-primary/60",
}

const getGameTheme = (gameName: string) => {
  const normalized = gameName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

  if (normalized.includes("primer bloque")) {
    return {
      gradient: "from-amber-500/25 to-zinc-900/10",
      icon: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
      ring: "ring-amber-500/30",
      bar: "from-amber-500 to-zinc-800",
    }
  }

  if (normalized.includes("primera era")) {
    return {
      gradient: "from-violet-500/20 to-purple-500/5",
      icon: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
      ring: "ring-violet-500/25",
      bar: "from-violet-500 to-purple-500",
    }
  }

  if (normalized.includes("imperio")) {
    return {
      gradient: "from-blue-500/20 to-cyan-500/5",
      icon: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
      ring: "ring-blue-500/25",
      bar: "from-blue-500 to-cyan-500",
    }
  }

  if (normalized.includes("furia extendido")) {
    return {
      gradient: "from-red-500/20 to-orange-500/10",
      icon: "bg-red-500/15 text-red-600 dark:text-red-400",
      ring: "ring-red-500/25",
      bar: "from-red-500 to-orange-500",
    }
  }

  return GAME_THEME_DEFAULT
}

const GamesSection = ({ games }: { games: PlayerGameStats[] }) => {
  const maxPoints = Math.max(...games.map((g) => g.points))
  return (
    <Card className="bg-card border border-border">
      <CardHeader className="flex flex-row items-center gap-2">
        <Gamepad2 className="h-5 w-5 text-primary" />
        <CardTitle className="text-foreground">Juegos</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {games.map((game) => {
            const colors = getGameTheme(game.name)
            const ptsPerTournament =
              game.tournaments > 0 ? Math.round(game.points / game.tournaments) : 0
            const pct = maxPoints > 0 ? (game.points / maxPoints) * 100 : 0
            return (
              <div
                key={game.name}
                className={`relative flex flex-col gap-3 p-5 rounded-xl border border-border bg-gradient-to-br ${colors.gradient} hover:shadow-md transition-all duration-300 overflow-hidden`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-11 w-11 rounded-2xl ${colors.icon} flex items-center justify-center shrink-0 ring-2 ${colors.ring}`}
                  >
                    <Gamepad2 className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-foreground leading-tight">{game.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {game.tournaments} torneos
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        {ptsPerTournament} pts/torneo
                      </span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xl font-bold text-foreground">
                      {game.points.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">puntos</p>
                  </div>
                </div>
                <div className="h-1.5 w-full bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${colors.bar} transition-all duration-700 ease-out`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

const RANK_COLORS = [
  {
    bg: "from-yellow-500 to-amber-400",
    badge: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
    bar: "from-yellow-500 to-amber-400",
  },
  {
    bg: "from-slate-400 to-slate-300",
    badge: "bg-slate-400/10 text-slate-600 dark:text-slate-300 border-slate-400/30",
    bar: "from-slate-400 to-slate-300",
  },
  {
    bg: "from-orange-600 to-orange-400",
    badge: "bg-orange-600/10 text-orange-700 dark:text-orange-400 border-orange-600/30",
    bar: "from-orange-600 to-orange-400",
  },
  {
    bg: "from-primary to-primary/60",
    badge: "bg-primary/10 text-primary border-primary/30",
    bar: "from-primary to-primary/60",
  },
]

const TournamentTypesSection = ({ types }: { types: PlayerTournamentTypeStats[] }) => {
  const sorted = [...types].sort((a, b) => b.points - a.points)

  return (
    <Card className="bg-card border border-border">
      <CardHeader className="flex flex-row items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <CardTitle className="text-foreground">Torneos por Tipo</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
          {sorted.map((type, index) => {
            const rank = RANK_COLORS[index] ?? RANK_COLORS[RANK_COLORS.length - 1]
            return (
              <div
                key={type.name}
                className="h-full rounded-lg border border-border/80 bg-background/60 px-3 py-3"
              >
                <div className="grid grid-cols-[28px_minmax(0,1fr)_120px] items-center gap-3">
                  <div
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 bg-gradient-to-br ${rank.bg} text-white shadow-sm`}
                    title={`Posicion #${index + 1}`}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate leading-tight min-h-5">
                      {type.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {type.tournaments.toLocaleString()} torneos jugados
                    </p>
                  </div>
                  <div className="w-[120px] text-right shrink-0">
                    <Badge className={`text-xs px-2 py-0.5 border font-semibold ${rank.badge}`}>
                      {type.points.toLocaleString()} pts
                    </Badge>
                    <p className="text-[11px] text-muted-foreground mt-1">Total puntos</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

const getWinRateColors = (winPct: number) => {
  if (winPct > 65) {
    return {
      bar: "from-emerald-500 to-emerald-400",
      text: "text-emerald-600 dark:text-emerald-400",
    }
  }

  if (winPct >= 35) {
    return {
      bar: "from-amber-500 to-yellow-400",
      text: "text-amber-600 dark:text-amber-400",
    }
  }

  return {
    bar: "from-red-500 to-rose-400",
    text: "text-red-600 dark:text-red-400",
  }
}

const getWinRateAnimation = (index: number, winPct: number) => {
  const duration = Math.min(1.35, 0.55 + index * 0.14 + winPct / 240)
  const delay = index * 0.04

  return { duration, delay }
}

const formatSeasonDate = (rawDate?: string) => {
  if (!rawDate) return "-"

  const date = new Date(Number(rawDate))
  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

const DetailedStatsSection = ({
  detailedStats,
  analyzedSeasons,
  allSeasons,
  selectedSeasonIds,
  onSeasonToggle,
  onClearSeasonIds,
  loading = false,
}: {
  detailedStats: DetailedGameStats[]
  analyzedSeasons: GameAnalyzedSeasons[]
  allSeasons: GameAnalyzedSeasons[]
  selectedSeasonIds: number[]
  onSeasonToggle: (seasonId: number) => void
  onClearSeasonIds: () => void
  loading?: boolean
}) => {
  const safeDetailedStats = useMemo(() => detailedStats ?? [], [detailedStats])
  const safeAnalyzedSeasons = useMemo(() => analyzedSeasons ?? [], [analyzedSeasons])
  const safeAllSeasons = useMemo(() => allSeasons ?? [], [allSeasons])

  const [activeTab, setActiveTab] = useState(safeDetailedStats[0]?.name ?? "")

  useEffect(() => {
    if (!safeDetailedStats.some((game) => game.name === activeTab)) {
      setActiveTab(safeDetailedStats[0]?.name ?? "")
    }
  }, [activeTab, safeDetailedStats])

  if (loading) return <DetailedStatsSkeleton />

  if (!safeDetailedStats.length) return null

  const activeGame =
    safeDetailedStats.find((game) => game.name === activeTab) ?? safeDetailedStats[0]
  // Use allSeasons (unfiltered) to always show all available season chips
  const activeGameSeasons = [
    ...(safeAllSeasons.find((game) => game.name === activeGame?.name)?.seasons ??
      safeAnalyzedSeasons.find((game) => game.name === activeGame?.name)?.seasons ??
      []),
  ].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())

  return (
    <Card className="bg-card border border-border">
      <CardHeader className="flex flex-row items-center gap-2">
        <TrendingUp className="h-5 w-5 text-primary" />
        <CardTitle className="text-foreground">Estadísticas Detalladas</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-muted/50 p-1 mb-4">
            {safeDetailedStats.map((game) => (
              <TabsTrigger
                key={game.name}
                value={game.name}
                className="min-w-[120px] max-w-[25%] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {game.name}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent key={activeGame.name} value={activeGame.name}>
            <div className="mb-4 rounded-lg border border-border/70 bg-muted/20 p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-foreground">Filtrar por temporada</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedSeasonIds.length === 0
                      ? "Mostrando todas las temporadas. Haz click en una para filtrar."
                      : `${selectedSeasonIds.length} temporada${selectedSeasonIds.length > 1 ? "s" : ""} seleccionada${selectedSeasonIds.length > 1 ? "s" : ""}. Haz click nuevamente para quitar.`}
                  </p>
                </div>
                {selectedSeasonIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onClearSeasonIds()}
                    className="shrink-0 rounded-md border border-border bg-background/70 px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
                  >
                    Reiniciar filtros
                  </button>
                )}
              </div>
              {activeGameSeasons.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {activeGameSeasons.map((season, i) => (
                    <motion.div
                      key={season.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, delay: i * 0.06, ease: "easeOut" }}
                      onClick={() => onSeasonToggle(season.id)}
                      className={`flex cursor-pointer flex-col justify-between rounded-md border px-2.5 py-2 transition-colors ${
                        selectedSeasonIds.includes(season.id)
                          ? "border-primary/60 bg-primary/10"
                          : "border-border/70 bg-background/60 hover:border-primary/30"
                      }`}
                    >
                      <div className="flex items-start gap-1.5">
                        {season.isActive ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Crown className="mt-0.5 w-3.5 h-3.5 shrink-0 text-emerald-500 fill-emerald-500 cursor-default" />
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>Temporada activa</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Archive className="mt-0.5 w-3.5 h-3.5 shrink-0 text-muted-foreground/50 cursor-default" />
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>Temporada inactiva</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Badge
                          variant="secondary"
                          className="text-[11px] font-medium leading-tight"
                        >
                          {season.code ? `${season.name} (${season.code})` : season.name}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1.5 pl-5">
                        Inicio: {formatSeasonDate(season.startDate)} · Fin:{" "}
                        {formatSeasonDate(season.endDate)}
                      </p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Sin temporadas activas analizadas.</p>
              )}
            </div>
            <div className="divide-y divide-border">
              {activeGame.types.map((type, index) => {
                const total = type.wins + type.losses + type.draws
                const winPct =
                  total > 0 ? Math.round(((type.wins + type.draws * 0.5) / total) * 100) : 0
                const winRateColors = getWinRateColors(winPct)
                const animation = getWinRateAnimation(index, winPct)

                return (
                  <div
                    key={type.name}
                    className="py-3 grid grid-cols-[minmax(0,1fr)_112px] gap-x-4 gap-y-1.5 items-center"
                  >
                    {/* Nombre + barra */}
                    <div className="min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground truncate">
                          {type.name}
                        </span>
                        <span
                          className={`text-xs ml-2 shrink-0 w-16 text-right tabular-nums ${winRateColors.text}`}
                        >
                          {winPct}% win
                        </span>
                      </div>
                      <div className="relative h-1.5">
                        <div className="h-full bg-muted rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full bg-gradient-to-r ${winRateColors.bar}`}
                            initial={{ width: "0%" }}
                            animate={{ width: `${winPct}%` }}
                            transition={{
                              duration: animation.duration,
                              delay: animation.delay,
                              ease: "easeOut",
                            }}
                          />
                        </div>

                        <div
                          className="absolute -inset-y-2 left-1/4 w-4 -translate-x-1/2 z-20 group/marker"
                          title="25%"
                          aria-label="Marcador 25%"
                        >
                          <span className="absolute inset-y-2 left-1/2 -translate-x-1/2 w-px bg-white/90 dark:bg-white/85 shadow-[0_0_0_1px_rgba(15,23,42,0.45)] dark:shadow-[0_0_0_1px_rgba(2,6,23,0.7)] pointer-events-none" />
                          <span className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-md border border-border bg-popover px-1.5 py-0.5 text-[10px] leading-none text-popover-foreground shadow-sm opacity-0 group-hover/marker:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                            25%
                          </span>
                        </div>

                        <div
                          className="absolute -inset-y-2 left-1/2 w-4 -translate-x-1/2 z-20 group/marker"
                          title="50%"
                          aria-label="Marcador 50%"
                        >
                          <span className="absolute inset-y-2 left-1/2 -translate-x-1/2 w-px bg-white/90 dark:bg-white/85 shadow-[0_0_0_1px_rgba(15,23,42,0.45)] dark:shadow-[0_0_0_1px_rgba(2,6,23,0.7)] pointer-events-none" />
                          <span className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-md border border-border bg-popover px-1.5 py-0.5 text-[10px] leading-none text-popover-foreground shadow-sm opacity-0 group-hover/marker:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                            50%
                          </span>
                        </div>

                        <div
                          className="absolute -inset-y-2 left-3/4 w-4 -translate-x-1/2 z-20 group/marker"
                          title="75%"
                          aria-label="Marcador 75%"
                        >
                          <span className="absolute inset-y-2 left-1/2 -translate-x-1/2 w-px bg-white/90 dark:bg-white/85 shadow-[0_0_0_1px_rgba(15,23,42,0.45)] dark:shadow-[0_0_0_1px_rgba(2,6,23,0.7)] pointer-events-none" />
                          <span className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-md border border-border bg-popover px-1.5 py-0.5 text-[10px] leading-none text-popover-foreground shadow-sm opacity-0 group-hover/marker:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                            75%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Stats compactos */}
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-foreground">
                        {type.points.toLocaleString()}{" "}
                        <span className="text-xs font-normal text-muted-foreground">pts</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="text-green-600 dark:text-green-400">{type.wins}</span>
                        <span className="mx-0.5 opacity-40">/</span>
                        <span className="text-red-600 dark:text-red-400">{type.losses}</span>
                        <span className="mx-0.5 opacity-40">/</span>
                        <span className="text-yellow-600 dark:text-yellow-400">{type.draws}</span>
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-border/60 space-y-1.5 text-[11px] text-muted-foreground">
              <p>
                <span className="font-medium text-foreground/80">¿Cómo se calcula el winrate?</span>{" "}
                Se suman todos los puntos obtenidos: cada victoria vale 1 punto completo y cada
                empate vale medio punto. Ese total se divide por la cantidad de partidas jugadas.
                Por ejemplo, 6 victorias y 2 empates en 10 partidas suman 7 puntos → 70% de winrate.
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500" />
                  Bajo (&lt; 35%)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  Medio (35%–65%)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Alto (&gt; 65%)
                </span>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

export const PlayerStatsDashboard = ({
  personId,
  playerCode,
  showPlayerIdentity = false,
}: PlayerStatsDashboardProps) => {
  const [selectedSeasonIds, setSelectedSeasonIds] = useState<number[]>([])
  const [allSeasons, setAllSeasons] = useState<GameAnalyzedSeasons[]>([])
  const [cachedStats, setCachedStats] = useState<PlayerStats | null>(null)
  const [pendingScroll, setPendingScroll] = useState(false)

  useEffect(() => {
    setSelectedSeasonIds([])
    setAllSeasons([])
    setCachedStats(null)
    setPendingScroll(false)
  }, [personId, playerCode])

  const { stats, loading, error } = usePlayerStats({
    personId,
    playerCode,
    seasonIds: selectedSeasonIds,
  })

  // Cache stats so we can keep displaying stale data while re-fetching
  useEffect(() => {
    if (!stats) return
    setCachedStats((prev) => (prev === stats ? prev : stats))
  }, [stats])

  // Cache the full season list from the first unfiltered response
  useEffect(() => {
    if (stats?.analyzedSeasons && selectedSeasonIds.length === 0) {
      setAllSeasons((prev) => {
        const next = stats.analyzedSeasons

        // Avoid state churn when Apollo returns equivalent arrays with new references.
        const sameStructure =
          prev.length === next.length &&
          prev.every((game, gameIndex) => {
            const nextGame = next[gameIndex]
            if (!nextGame || game.name !== nextGame.name) return false
            if (game.seasons.length !== nextGame.seasons.length) return false

            return game.seasons.every((season, seasonIndex) => {
              const nextSeason = nextGame.seasons[seasonIndex]
              return (
                !!nextSeason &&
                season.id === nextSeason.id &&
                season.code === nextSeason.code &&
                season.isActive === nextSeason.isActive
              )
            })
          })

        return sameStructure ? prev : next
      })
    }
  }, [stats?.analyzedSeasons, selectedSeasonIds.length])

  // Scroll to top once loading finishes after a filter change
  useEffect(() => {
    if (!loading && pendingScroll) {
      setPendingScroll(false)
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [loading, pendingScroll])

  const scrollToTopAfterResults = () => {
    setPendingScroll(true)
  }

  const handleSeasonToggle = (seasonId: number) => {
    setSelectedSeasonIds((prev) =>
      prev.includes(seasonId) ? prev.filter((id) => id !== seasonId) : [...prev, seasonId],
    )
    scrollToTopAfterResults()
  }

  // Initial load (no data yet) → full skeleton
  if (loading && !cachedStats) return <DashboardSkeleton />

  // Use cached data while re-fetching so other sections stay visible
  const displayStats = stats ?? cachedStats

  if (error) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-8 text-center">
          <p className="text-destructive">Error al cargar estadísticas: {error.message}</p>
        </CardContent>
      </Card>
    )
  }

  if (!displayStats) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">No hay estadísticas disponibles</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border/70 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 md:p-5">
        <h2 className="text-lg md:text-xl font-bold text-foreground">Resumen del jugador</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Aquí verás una vista general del rendimiento competitivo: torneos jugados, puntos
          acumulados, winrate y desglose detallado por juego y tipo de torneo.
        </p>
      </div>
      {showPlayerIdentity && displayStats.player ? (
        <PlayerIdentityCard player={displayStats.player} />
      ) : null}
      <SummaryCards totals={displayStats.totals} />
      <GamesSection games={displayStats.games} />
      <TournamentTypesSection types={displayStats.tournamentTypes} />
      <DetailedStatsSection
        detailedStats={displayStats.detailedStats}
        analyzedSeasons={displayStats.analyzedSeasons}
        allSeasons={allSeasons}
        selectedSeasonIds={selectedSeasonIds}
        onSeasonToggle={handleSeasonToggle}
        onClearSeasonIds={() => {
          setSelectedSeasonIds([])
          scrollToTopAfterResults()
        }}
        loading={loading}
      />
      <p className="text-xs text-muted-foreground text-center px-4">
        * Las estadísticas incluyen únicamente torneos{" "}
        <span className="font-medium">presenciales</span>. Los torneos online no son considerados en
        estos registros.
      </p>
    </div>
  )
}
